import {
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Mesh,
  MeshBasicMaterial,
  Points,
  PointsMaterial,
  Vector3,
} from "three";
import { Line2 } from "three/addons/lines/Line2.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";
import type { Viewer } from "./Viewer";

export type MeasureMode = "line" | "area" | "volume";

export interface MeasureResult {
  mode: MeasureMode;
  pointCount: number;
  /** True once the shape has been locked (Finish), so no further points are added. */
  closed: boolean;
  /** Path length (line), or polygon perimeter once closed, in metres. */
  length: number;
  /** Horizontal (plan) area enclosed by the polygon, in square metres. `null` under 3 points. */
  area: number | null;
  /** Estimated volume between the picked surface and the base elevation, in cubic metres. */
  volume: number | null;
  /** Base elevation used for the volume estimate, as a real project height (metres). */
  base: number | null;
}

/** A point in the project's real projected CRS, e.g. Swiss LV95 east/north/height. */
export interface RealPoint {
  east: number;
  north: number;
  height: number;
}

/** A finished shape persisted to the measurements file, independent of any scene origin. */
export interface SavedMeasurement {
  id: string;
  name: string;
  /** Label of the model (mesh or ortho) this measurement was taken against, e.g. "site.zip". */
  modelLabel: string;
  mode: MeasureMode;
  points: RealPoint[];
  base: number | null;
  length: number;
  area: number | null;
  volume: number | null;
  savedAt: string;
}

export const MEASURE_COLOR: Record<MeasureMode, number> = {
  line: 0x2f9bff,
  area: 0x2ecc71,
  volume: 0xffa733,
};

/** Drawing order among the tool's own transparent objects — highest paints last, on top. */
const ORDER = { fill: 10, line: 20, preview: 21, pointsHalo: 30, pointsCore: 31 };

/** Screen-space distance, in CSS pixels, a click/drag has to land within an existing point's
 *  marker to grab it instead of placing a new point or panning the camera. */
const POINT_GRAB_RADIUS = 14;

/**
 * Interactive line / area / volume measuring.
 *
 * Points are picked against whatever is loaded (terrain or mesh) with the viewer's own
 * raycaster and stored in the scene's local, unscaled frame — the same frame the HUD
 * coordinate readout uses — so lengths, areas and volumes come out in real metres
 * regardless of the vertical exaggeration slider; the drawn shape still stretches with the
 * terrain because its group is parented under `viewer.world`.
 *
 * Every visual (points, lines, fill) is drawn with depth testing off and a high render
 * order: the points/lines sit exactly ON the picked surface, so with depth testing on they
 * z-fight against the opaque terrain/mesh they were picked against and can disappear into
 * it entirely depending on floating-point rounding. Drawing them "on top" regardless of
 * what's in front is what actually makes a measurement visible against the model. Lines are
 * `Line2` (three.js's fat-line addon) rather than plain `Line`, because a stock WebGL
 * `LineBasicMaterial` is stuck at 1 physical pixel wide in every browser regardless of its
 * `linewidth` — Line2 renders an actual screen-space ribbon, which is also what makes the
 * points and lines chunky enough to see against a busy textured model.
 *
 * The tool's own graphics are excluded from `Viewer.pick()` (see `excludeFromPicking`):
 * the whole group is parented under `viewer.world` so it inherits the vertical-exaggeration
 * scale, but that means it would otherwise show up in the same raycast used to place the
 * *next* point, letting an existing marker or the translucent fill steal a click meant for
 * the terrain underneath it.
 *
 * Area is the horizontal (plan) projection of the picked polygon, not a true surface area.
 * Volume is a fan-triangulated estimate between the picked points and a base elevation
 * (defaulting to the lowest point clicked, editable): accuracy depends on how well the
 * clicked points sample the real surface — this is a quick estimate, not a survey-grade
 * cut/fill integration over the terrain mesh.
 */
export class MeasureTool {
  mode: MeasureMode | null = null;
  onChange: ((result: MeasureResult | null) => void) | null = null;

  private points: Vector3[] = [];
  private preview: Vector3 | null = null;
  /**
   * What's actually drawn for `points`/`preview` — identical except where a point was
   * picked against the (flat-drawn) terrain, where the height is the terrain's flat display
   * elevation instead of the real one `points` carries. Keeps a measurement lying flush
   * against the flat orthophoto/terrain plate on screen while `points` still holds the real
   * elevation everywhere else (length/area/volume math, saving). See `Viewer.terrainDisplayY`.
   */
  private displayPoints: Vector3[] = [];
  private displayPreview: Vector3 | null = null;
  private closed = false;
  /** Base elevation for the volume estimate, in the local (unscaled) frame. */
  private base: number | null = null;

  private readonly group = new Group();
  private visuals: Array<Points | Line2 | Mesh> = [];

  private dragStart: { x: number; y: number } | null = null;
  /** Index into `points`/`displayPoints` of the marker currently being dragged, if any. */
  private draggingIndex: number | null = null;

  constructor(private readonly viewer: Viewer) {
    viewer.world.add(this.group);
    this.installListeners();
  }

  /** Activates a mode (clearing any previous shape) or, passed `null`, turns measuring off. */
  setMode(mode: MeasureMode | null): void {
    this.mode = mode;
    this.points = [];
    this.displayPoints = [];
    this.preview = null;
    this.displayPreview = null;
    this.closed = false;
    this.base = null;
    this.draggingIndex = null;
    this.viewer.canvas.style.cursor = "";
    this.rebuild();
    this.viewer.requestRender();
  }

  /** Removes every point of the current shape, keeping the active mode. */
  clear(): void {
    if (!this.mode) return;
    this.setMode(this.mode);
    this.emit();
  }

  undo(): void {
    if (this.closed || this.points.length === 0) return;
    this.points.pop();
    this.displayPoints.pop();
    this.base = this.points.length > 0 ? Math.min(...this.points.map((p) => p.y)) : null;
    this.rebuild();
    this.emit();
    this.viewer.requestRender();
  }

  /** Locks the shape: closes the polygon loop (area/volume) and stops accepting points. */
  finish(): void {
    const minPoints = this.mode === "line" ? 2 : 3;
    if (!this.mode || this.points.length < minPoints) return;
    this.closed = true;
    this.preview = null;
    this.displayPreview = null;
    this.rebuild();
    this.emit();
    this.viewer.requestRender();
  }

  /** True once the current shape is locked, i.e. eligible to be saved. */
  get isClosed(): boolean {
    return this.closed;
  }

  /**
   * Re-opens a finished shape for further editing: `undo` can trim points again and further
   * clicks extend it, exactly as before `finish()` was called. This is what makes "edit" on
   * a loaded/saved measurement mean something beyond renaming it.
   */
  reopen(): void {
    if (!this.mode || !this.closed) return;
    this.closed = false;
    this.rebuild();
    this.emit();
    this.viewer.requestRender();
  }

  /** Base elevation for the volume estimate, as a real project height (metres). */
  getBaseReal(): number | null {
    return this.base === null ? null : this.base + this.viewer.origin.height;
  }

  /** The current shape's points in the project's real CRS, or `null` with nothing drawn. */
  getRealPoints(): RealPoint[] | null {
    if (this.points.length === 0) return null;
    return this.points.map((p) => this.viewer.origin.toProjected(p));
  }

  /**
   * Replaces the current shape with a previously saved one. The scene origin has to be
   * established already (by some dataset having loaded) for the real coordinates to be
   * placed correctly, same as any other georeferenced content.
   */
  loadSaved(mode: MeasureMode, points: RealPoint[], closed: boolean, baseReal: number | null): void {
    this.mode = mode;
    this.points = points.map((p) => this.viewer.origin.toScene(p.east, p.north, p.height));
    // No live pick to say whether each point came off the terrain or a mesh — a loaded
    // dataset is overwhelmingly one or the other, so if terrain is currently drawn flat,
    // draw every restored point flush with it too (see the `displayPoints` field comment).
    this.displayPoints = this.points.map((p) => this.toDisplay(p));
    this.preview = null;
    this.displayPreview = null;
    this.closed = closed;
    this.draggingIndex = null;
    this.base = baseReal === null ? null : baseReal - this.viewer.origin.height;
    this.rebuild();
    this.emit();
    this.viewer.requestRender();
  }

  setBaseReal(height: number): void {
    if (this.points.length === 0) return;
    this.base = height - this.viewer.origin.height;
    this.rebuild();
    this.emit();
    this.viewer.requestRender();
  }

  result(): MeasureResult | null {
    if (!this.mode || this.points.length === 0) return null;
    const shown = this.closed ? this.points : this.previewPoints();

    let length = 0;
    for (let i = 1; i < shown.length; i++) length += shown[i].distanceTo(shown[i - 1]);
    if (this.mode !== "line" && this.closed && shown.length >= 3) {
      length += shown[shown.length - 1].distanceTo(shown[0]);
    }

    let area: number | null = null;
    let volume: number | null = null;
    if (this.mode !== "line" && shown.length >= 3) {
      area = 0;
      volume = this.mode === "volume" && this.base !== null ? 0 : null;
      for (let i = 1; i < shown.length - 1; i++) {
        const [a, b, c] = [shown[0], shown[i], shown[i + 1]];
        const triArea = planArea(a, b, c);
        area += triArea;
        if (volume !== null && this.base !== null) {
          volume += triArea * Math.max(0, (a.y + b.y + c.y) / 3 - this.base);
        }
      }
    }

    return {
      mode: this.mode,
      pointCount: this.points.length,
      closed: this.closed,
      length,
      area,
      volume,
      base: this.getBaseReal(),
    };
  }

  dispose(): void {
    if (this.draggingIndex !== null) this.viewer.controls.enabled = true;
    this.viewer.canvas.style.cursor = "";
    this.disposeVisuals();
    this.viewer.world.remove(this.group);
  }

  // ------------------------------------------------------------ internals

  private installListeners(): void {
    const canvas = this.viewer.canvas;
    canvas.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      // Grabbing an existing marker takes priority over placing a new point or panning —
      // this is what makes a loaded/selected measurement's points directly draggable rather
      // than only editable by undoing from the end (see `reopen`).
      if (this.mode) {
        const index = this.pointNear(event.clientX, event.clientY);
        if (index !== null) {
          this.draggingIndex = index;
          this.viewer.controls.enabled = false;
          canvas.setPointerCapture(event.pointerId);
          canvas.style.cursor = "grabbing";
          event.preventDefault();
          return;
        }
      }
      this.dragStart = { x: event.clientX, y: event.clientY };
    });
    canvas.addEventListener("pointerup", (event) => {
      if (this.draggingIndex !== null) {
        this.endDrag(event.pointerId);
        return;
      }
      const start = this.dragStart;
      this.dragStart = null;
      if (!this.mode || this.closed || event.button !== 0 || !start) return;
      // A drag (pan/orbit) moves the pointer; only a near-stationary click adds a point.
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4) return;
      this.addPoint(event.clientX, event.clientY);
    });
    canvas.addEventListener("pointercancel", (event) => {
      if (this.draggingIndex !== null) this.endDrag(event.pointerId);
    });
    canvas.addEventListener("pointermove", (event) => {
      if (this.draggingIndex !== null) {
        this.dragPoint(event.clientX, event.clientY);
        return;
      }
      if (this.mode && !this.closed && this.points.length > 0) {
        const hit = this.viewer.pick(event.clientX, event.clientY);
        this.preview = hit ? this.toLocal(hit.point) : null;
        this.displayPreview = hit ? this.toDisplayFromHit(this.preview!, hit.displayY) : null;
        this.rebuild();
        this.emit();
      }
      // Hovering an existing marker gets a grab cursor even outside an active draw, so a
      // loaded measurement's points read as draggable at a glance.
      if (this.mode) canvas.style.cursor = this.pointNear(event.clientX, event.clientY) !== null ? "grab" : "";
    });
    canvas.addEventListener("pointerleave", () => {
      if (this.draggingIndex !== null) return;
      canvas.style.cursor = "";
      if (!this.preview) return;
      this.preview = null;
      this.displayPreview = null;
      this.rebuild();
      this.emit();
    });
  }

  private toLocal(worldPoint: Vector3): Vector3 {
    return this.viewer.world.worldToLocal(worldPoint.clone());
  }

  /** `local`'s display counterpart when it isn't the result of a live pick (e.g. a point
   *  just restored from a save) — flush with the terrain's flat height when one is loaded,
   *  otherwise identical to the real point (mesh geometry is drawn at its true height). */
  private toDisplay(local: Vector3): Vector3 {
    const flatY = this.viewer.terrainDisplayY;
    return flatY === null ? local.clone() : local.clone().setY(flatY);
  }

  /** Same as `toDisplay`, but for a point that *was* just picked — `displayY`, from
   *  `Viewer.pick()`, says definitively whether that particular hit landed on the flat
   *  terrain rather than guessing from whatever is loaded. */
  private toDisplayFromHit(local: Vector3, displayY: number | null): Vector3 {
    return displayY === null ? local.clone() : local.clone().setY(displayY);
  }

  private addPoint(clientX: number, clientY: number): void {
    const hit = this.viewer.pick(clientX, clientY);
    if (!hit || !this.mode) return;
    const local = this.toLocal(hit.point);
    this.points.push(local);
    this.displayPoints.push(this.toDisplayFromHit(local, hit.displayY));
    this.base = this.base === null ? local.y : Math.min(this.base, local.y);
    this.preview = null;
    this.displayPreview = null;
    this.rebuild();
    this.emit();
    this.viewer.requestRender();
  }

  /** Committed points plus the live hover point, for the rubber-band preview and readout. */
  private previewPoints(): Vector3[] {
    return this.preview ? [...this.points, this.preview] : this.points;
  }

  /** Display counterpart of `previewPoints()`, for what's actually drawn. */
  private displayPreviewPoints(): Vector3[] {
    return this.displayPreview ? [...this.displayPoints, this.displayPreview] : this.displayPoints;
  }

  /** Index of the point marker nearest the pointer, within `POINT_GRAB_RADIUS` screen
   *  pixels, or `null` if none is close enough — used to start a drag on pointerdown and to
   *  show a grab cursor on hover. */
  private pointNear(clientX: number, clientY: number): number | null {
    let best: { index: number; dist: number } | null = null;
    for (let i = 0; i < this.displayPoints.length; i++) {
      const screen = this.toScreen(this.displayPoints[i]);
      if (!screen) continue;
      const dist = Math.hypot(screen.x - clientX, screen.y - clientY);
      if (dist <= POINT_GRAB_RADIUS && (!best || dist < best.dist)) best = { index: i, dist };
    }
    return best?.index ?? null;
  }

  /** Projects a local-frame point to client (CSS pixel) coordinates, or `null` when it falls
   *  outside the camera's near/far range (behind the camera, mainly). */
  private toScreen(local: Vector3): { x: number; y: number } | null {
    const ndc = this.viewer.world.localToWorld(local.clone()).project(this.viewer.camera);
    if (ndc.z < -1 || ndc.z > 1) return null;
    const rect = this.viewer.canvas.getBoundingClientRect();
    return { x: rect.left + ((ndc.x + 1) / 2) * rect.width, y: rect.top + ((1 - ndc.y) / 2) * rect.height };
  }

  private dragPoint(clientX: number, clientY: number): void {
    const index = this.draggingIndex;
    if (index === null) return;
    const hit = this.viewer.pick(clientX, clientY);
    if (!hit) return;
    const local = this.toLocal(hit.point);
    this.points[index] = local;
    this.displayPoints[index] = this.toDisplayFromHit(local, hit.displayY);
    this.rebuild();
    this.emit();
    this.viewer.requestRender();
  }

  private endDrag(pointerId: number): void {
    this.draggingIndex = null;
    this.viewer.controls.enabled = true;
    this.viewer.canvas.style.cursor = "";
    if (this.viewer.canvas.hasPointerCapture(pointerId)) this.viewer.canvas.releasePointerCapture(pointerId);
  }

  private rebuild(): void {
    this.disposeVisuals();
    if (!this.mode || this.displayPoints.length === 0) return;
    const color = MEASURE_COLOR[this.mode];
    const points = this.displayPoints;
    const preview = this.displayPreview;

    // A dark halo behind a coloured core: chunky enough to read at a glance, and legible
    // against both a bright orthophoto and a dark, unlit mesh.
    this.addVisual(pointsMesh(points, 0x0b1016, 16), ORDER.pointsHalo);
    this.addVisual(pointsMesh(points, color, 9), ORDER.pointsCore);

    if (points.length >= 2) {
      const loop = this.mode !== "line" && this.closed ? [...points, points[0]] : points;
      this.addVisual(fatLine(loop, color, 5, false), ORDER.line);
    }

    if (!this.closed && preview) {
      const tail =
        this.mode === "line" || points.length < 2
          ? [points[points.length - 1], preview]
          : [points[points.length - 1], preview, points[0]];
      this.addVisual(fatLine(tail, color, 3, true), ORDER.preview);
    }

    const shown = this.closed ? points : this.displayPreviewPoints();
    if (this.mode !== "line" && shown.length >= 3) {
      const positions: number[] = [];
      for (let i = 1; i < shown.length - 1; i++) {
        positions.push(...vec(shown[0]), ...vec(shown[i]), ...vec(shown[i + 1]));
      }
      const fillGeom = new BufferGeometry();
      fillGeom.setAttribute("position", new Float32BufferAttribute(positions, 3));
      const fill = new Mesh(
        fillGeom,
        new MeshBasicMaterial({
          color,
          side: DoubleSide,
          transparent: true,
          opacity: 0.28,
          depthWrite: false,
          depthTest: false,
        }),
      );
      this.addVisual(fill, ORDER.fill);
    }
  }

  /** Adds a visual to the scene, on top regardless of what's in front, and out of `pick()`'s way. */
  private addVisual(obj: Points | Line2 | Mesh, renderOrder: number): void {
    obj.renderOrder = renderOrder;
    // See the class comment: the tool's own graphics must never be able to steal a raycast
    // hit meant for the terrain/mesh underneath them.
    obj.raycast = () => {};
    this.group.add(obj);
    this.visuals.push(obj);
  }

  private disposeVisuals(): void {
    for (const obj of this.visuals) {
      this.group.remove(obj);
      obj.geometry.dispose();
      for (const material of Array.isArray(obj.material) ? obj.material : [obj.material]) material.dispose();
    }
    this.visuals = [];
  }

  private emit(): void {
    this.onChange?.(this.result());
  }
}

function flatten(points: Vector3[]): number[] {
  const out: number[] = [];
  for (const p of points) out.push(p.x, p.y, p.z);
  return out;
}

export function vec(p: Vector3): [number, number, number] {
  return [p.x, p.y, p.z];
}

/** Screen-space-sized point markers, depth-tested off so they always sit on top. */
export function pointsMesh(points: Vector3[], color: number, size: number): Points {
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute(flatten(points), 3));
  return new Points(
    geometry,
    new PointsMaterial({ color, size, sizeAttenuation: false, depthTest: false, transparent: true }),
  );
}

/**
 * A thick screen-space line (three.js's `Line2`/`LineMaterial` "fat lines" addon) — a plain
 * `Line`/`LineBasicMaterial` is capped at 1 physical pixel in every browser's WebGL
 * implementation regardless of its `linewidth` property, which is what made earlier
 * measurements next to impossible to spot against a textured model.
 */
export function fatLine(points: Vector3[], color: number, widthPx: number, dashed: boolean, opacity?: number): Line2 {
  const geometry = new LineGeometry();
  geometry.setPositions(flatten(points));
  const material = new LineMaterial({
    color,
    linewidth: widthPx,
    transparent: true,
    opacity: opacity ?? (dashed ? 0.85 : 1),
    depthTest: false,
    dashed,
    ...(dashed ? { dashScale: 4, dashSize: 1, gapSize: 0.6 } : {}),
  });
  // LineMaterial needs the renderer's viewport size to turn `linewidth` (screen pixels) into
  // clip-space; Line2/LineSegments2 refreshes `material.resolution` from it every frame in
  // its own onBeforeRender, so nothing further is needed here even across a window resize.
  const line = new Line2(geometry, material);
  line.computeLineDistances();
  return line;
}

/** Horizontal (plan) area of a triangle: the x/z (E/N) footprint, ignoring height. */
function planArea(a: Vector3, b: Vector3, c: Vector3): number {
  return Math.abs((b.x - a.x) * (c.z - a.z) - (c.x - a.x) * (b.z - a.z)) / 2;
}
